import EventEmitter from 'eventemitter3'
import gsap from 'gsap'
import * as PIXI from 'pixi.js'
import ArrayUtil from '../../../util/ArrayUtil'
import MyApplication from '../../../pixi/MyApplication'
import AssetsManager from '../../../pixi/AssetsManager'
import CardModel from '../data/CardModel'
import { CARD_TYPE } from '../constant/CardType'
import CardView from './CardView'
import Stacs from './Stacs'
import FinishArea from './FinishArea'
import TouchUtil from '../../../util/TouchUtil'
import StockArea from './StockArea'
import HeartView from './HeartView'
import BtnView from '../top/BtnView'
import EffectView from './EffectView'

/**
 * GameViewクラス
 */
class GameView extends PIXI.Container {
  // ---------------------------------------------------------------------------------------------------------
  // --	VARS
  // ---------------------------------------------------------------------------------------------------------

  //イベントエミッター
  private _emitter: EventEmitter = new EventEmitter()
  public get emitter(): EventEmitter {
    return this._emitter
  }

  //アプリケーション
  private _app: PIXI.Application

  //スコアボックス
  private _scoreContainer: PIXI.Container
  private _scoreBase: PIXI.Graphics
  private _scoreBox: PIXI.Text

  //コンテナ
  private _container: PIXI.Container

  //終了エリア
  private _finishAreas: PIXI.Container

  //ストックエリア
  private _stockArea: StockArea

  //スタックエリア
  private _stackArea: PIXI.Container

  //ドラッグ中エリア
  private _dragArea: PIXI.Container
  private _draggingCards: PIXI.Container

  //演出用コンテナ
  private _effectContainer: PIXI.Container

  //フラッシュ
  private _flash: PIXI.Graphics

  //顔
  private _face: PIXI.AnimatedSprite

  //キャラクター
  private _characterContainer: PIXI.Container
  private _character: PIXI.AnimatedSprite
  private _serif: PIXI.Sprite

  //ハートの演出用コンテナ
  private _heartContainer: PIXI.Container

  //リトライボタン
  private _retryBtn: BtnView

  //元々所属していたスタック
  private _originStacs: any

  //ドラッグ用の設定
  private _dragDiff: PIXI.Point = new PIXI.Point()

  //バインドされたメソッド
  private _onDrag: Function
  private _onDragEnd: Function

  //終了したカードの数
  private _finishCount: number = 0

  // ---------------------------------------------------------------------------------------------------------
  // --	GameView
  // ---------------------------------------------------------------------------------------------------------

  /**
   * コンストラクタ
   */
  constructor() {
    super()

    //app参照
    this._app = MyApplication.app

    //カードデータを生成
    let cards: Array<CardModel> = []
    for (let i = 0; i < 52; i++) {
      let type: CARD_TYPE
      if (i < 13) {
        type = CARD_TYPE.HEART
      } else if (i < 26) {
        type = CARD_TYPE.DIAMOND
      } else if (i < 39) {
        type = CARD_TYPE.SPADE
      } else {
        type = CARD_TYPE.CLUB
      }
      const card: CardModel = new CardModel(type, (i % 13) + 1)
      cards.push(card)
    }

    //カードをシャッフル
    cards = ArrayUtil.shuffleArray(cards)

    //ヒットエリア
    const ha: PIXI.Graphics = new PIXI.Graphics()
    ha.beginFill(0x000000)
    ha.drawRect(0, 0, 640, 1200)
    ha.endFill()
    ha.alpha = 0.0
    this.addChild(ha)

    //コンテナ
    this._container = new PIXI.Container()
    this.addChild(this._container)

    //終了エリア
    this._finishAreas = new PIXI.Container()
    this._container.addChild(this._finishAreas)

    //終了エリアを配置
    for (let index = 0; index < 4; index++) {
      const finishArea: FinishArea = new FinishArea()
      finishArea.x = 19 * 3 + 29 * 3 * index
      finishArea.y = 26 * 3 + 100
      this._finishAreas.addChild(finishArea)
    }

    //スタックエリア配置
    this._stackArea = new PIXI.Container()
    this._container.addChild(this._stackArea)

    //スタック配置
    for (let index = 0; index < 7; index++) {
      //スタック生成
      const stacs: Stacs = new Stacs()

      //配置
      stacs.x = 19 * 3 + 29 * 3 * index
      stacs.y = 72 * 3 + 150
      this._stackArea.addChild(stacs)

      //カードを抜き出して初期化
      let stacCards: Array<CardModel> = []
      for (let i = 0; i < index + 1; i++) {
        const card: CardModel = cards.pop()
        const cardView: CardView = card.view
        stacCards.push(card)
      }
      stacs.init(stacCards)

      //リスナー登録
      stacs.on('SELECT_CARD' as any, this.onSelectCard.bind(this))
    }

    //ストックエリア配置
    this._stockArea = new StockArea(cards)
    this._stockArea.x = 19 * 3 + 29 * 3 * 6
    this._stockArea.y = 26 * 3 + 100
    this._container.addChild(this._stockArea)
    this._stockArea.on('SELECT_CARD' as any, this.onSelectCard.bind(this))

    //演出用コンテナ
    this._effectContainer = new PIXI.Container()
    this._container.addChild(this._effectContainer)

    //フラッシュ
    this._flash = new PIXI.Graphics()
    this._flash.beginFill(0xffff00)
    this._flash.drawRect(0, 0, this._app.screen.width, this._app.screen.height * 2)
    this._flash.endFill()
    this._flash.alpha = 0.0
    this._flash.visible = false
    this._container.addChild(this._flash)

    //顔
    this._face = AssetsManager.instance.createMovieClipFromAnimation('ss', 'face')
    this._face.anchor.set(0.5)
    this._face.visible = false
    this._container.addChild(this._face)

    //ドラッグエリア
    this._dragArea = new PIXI.Container()
    this._container.addChild(this._dragArea)

    //キャラクター配置
    this._characterContainer = new PIXI.Container()
    this.addChild(this._characterContainer)
    this._character = AssetsManager.instance.createMovieClipFromAnimation('ss', 'bunny')
    this._character.animationSpeed = 0.1
    this._character.anchor.set(0.5, 0.9)
    this._character.scale.set(8.0)
    this._character.play()
    this._characterContainer.addChild(this._character)
    this._serif = AssetsManager.instance.createSpriteByName('serif.png')
    this._serif.anchor.set(0.5, 1)
    this._serif.scale.set(6.0)
    this._serif.y = -this._character.height * 0.9
    this._characterContainer.addChild(this._serif)

    //ハートの演出用コンテナ
    this._heartContainer = new PIXI.Container()
    this._heartContainer.y = -this._character.height * 0.6
    this._characterContainer.addChildAt(this._heartContainer, 0)

    //リトライボタン
    this._retryBtn = new BtnView('RETRY')
    this._retryBtn.y = -100
    this._retryBtn.visible = false
    this._retryBtn.interactive = true
    this._retryBtn.cursor = 'pointer'
    this._characterContainer.addChild(this._retryBtn)
    this._retryBtn.on('mousedown', (e) => {
      window.location.reload()
    })
    this._retryBtn.on('touchstart', (e) => {
      window.location.reload()
    })

    //バインドされたメソッドを保持
    this._onDrag = this.onDrag.bind(this)
    this._onDragEnd = this.onDragEnd.bind(this)

    //リセット
    this.reset()
  }

  /**
   * リセット
   */
  reset() {
    //変数初期化
    this._originStacs = null

    //キャラクター非表示
    this._character.y = this._character.height
    this._character.visible = false
    this._serif.visible = false

    //非表示
    this.visible = false
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 開く/閉じる
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * 開く
   */
  open() {
    this.visible = true
  }

  /**
   * 閉じる
   */
  close() {
    //リセット
    this.reset()
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 GAME
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * スタート
   */
  start() {
    console.log('start')

    gsap.delayedCall(1.0, () => {
      let len = this._stackArea.children.length
      for (let i = 0; i < len; i++) {
        //
        const stacs: Stacs = this._stackArea.getChildAt(i) as Stacs
        stacs.open()
      }
    })

    //スコア表示
    // this._scoreContainer.visible = true

    //入力受付
    this._app.stage.interactive = true
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 入力
  // --------------------------------------------------------------------------------------------------------------------------------------

  onSelectCard(data) {
    // console.log('SELECT_CARD', data)
    // console.log(TouchUtil.global)

    //SE
    AssetsManager.instance.playSound('sound_pickup')

    this._originStacs = data.target
    const cards: Array<CardView> = data.cards

    //カードの一枚目
    const card: CardView = cards[0]

    //カードのグローバル座標
    const global: PIXI.Point = card.toGlobal(new PIXI.Point(0, 0))

    //タッチ座標のズレを保存
    this._dragDiff.x = TouchUtil.global.x - global.x
    this._dragDiff.y = TouchUtil.global.y - global.y

    //ドラッグ用のコンテナ生成
    this._draggingCards = new PIXI.Container()
    this._draggingCards.x = global.x
    this._draggingCards.y = global.y
    this._dragArea.addChild(this._draggingCards)

    //カードを格納する
    let posY: number = 0
    cards.forEach((card: CardView) => {
      card.x = 0
      card.y = posY
      this._draggingCards.addChild(card)
      posY += card.isOmote ? 40 : 10
    })

    //リスナー登録
    this._app.stage.on('pointermove', this.onDrag.bind(this))
    this._app.stage.on('pointerup', this.onDragEnd.bind(this))
    this._app.stage.on('pointercancel', this.onDragEnd.bind(this))
    this._app.stage.on('pointerleave', this.onDragEnd.bind(this))
  }

  onDrag(e) {
    //IDが違う場合は無視
    if (TouchUtil.touchID != e.pointerId) return

    //タッチ座標
    const p: PIXI.Point = e.global
    TouchUtil.global.copyFrom(e.global)

    //ドラッグ
    this._draggingCards.x = p.x - this._dragDiff.x
    this._draggingCards.y = p.y - this._dragDiff.y

    //Stacsにヒットしているか
    this._stackArea.children.forEach((stacs: Stacs) => {
      stacs.hitTest(this._draggingCards.position)
    })

    //FinishAreaにヒットしているか
    this._finishAreas.children.forEach((finishArea: FinishArea) => {
      finishArea.hitTest(this._draggingCards.position)
    })
  }

  onDragEnd(e) {
    //IDリセット
    if (TouchUtil.touchID != e.pointerId) return
    TouchUtil.touchID = -1

    //SE
    AssetsManager.instance.playSound('sound_put')

    //リスナー登録解除
    this._app.stage.off('pointermove')
    this._app.stage.off('pointerup')
    this._app.stage.off('pointercancel')
    this._app.stage.off('pointerleave')

    //FinishAreaにヒットしているか（１枚ドラッグしている時だけ）
    let draggedFinishArea: FinishArea = null
    this._finishAreas.children.forEach((finishArea: FinishArea) => {
      if (finishArea.hitTest(this._draggingCards.position)) {
        draggedFinishArea = finishArea
        finishArea.unfocus()
      }
    })

    //FinishAreaにドラッグされた場合はカードを追加して終了
    if (draggedFinishArea) {
      //カウント
      this._finishCount += this._draggingCards.children.length
      // console.log('this._finishCount :', this._finishCount)

      //FinishAreaにカードを追加
      draggedFinishArea.addCards(this._draggingCards.children.slice() as Array<CardView>)

      //演出
      const effect: EffectView = new EffectView()
      effect.x = draggedFinishArea.x
      effect.y = draggedFinishArea.y
      this._effectContainer.addChild(effect)

      //演出
      this.emitter.emit('MISS')

      //フラッシュ
      gsap.killTweensOf(this._flash)
      this._flash.alpha = 0.0
      this._flash.visible = true
      gsap.delayedCall(0.4, () => {
        AssetsManager.instance.playSound('sound_effect')
        this._flash.alpha = 0.5
        gsap.to(this._flash, {
          alpha: 0.0,
          duration: 0.3,
          ease: 'power3.in',
          onComplete: () => {
            this._flash.visible = false
          },
        })
      })

      //顔
      gsap.killTweensOf(this._face)
      this._face.alpha = 0.0
      this._face.visible = true
      this._face.gotoAndStop(Math.floor(Math.random() * 7))
      gsap.delayedCall(0.4, () => {
        this._face.alpha = 0.5
        gsap.to(this._face, {
          alpha: 0.0,
          duration: 0.3,
          ease: 'power3.in',
          onComplete: () => {
            this._face.visible = false
          },
        })
      })

      //Stacsの一番上を表にする
      this._stackArea.children.forEach((stacs: Stacs) => {
        stacs.open()
      })

      //終了判定
      // if (this._finishCount >= 1) this.finish()
      if (this._finishCount == 52) {
        gsap.delayedCall(1.0, () => {
          this.finish()
        })
      }

      return
    }

    //ドラッグ先のStacsを取得
    let draggedStacs: any = null
    this._stackArea.children.forEach((stacs: Stacs) => {
      if (stacs.hitTest(this._draggingCards.position)) {
        draggedStacs = stacs
        draggedStacs.unfocus()
      }
    })

    //ドラッグ先のStacsがない場合は元のStacsに戻す
    if (!draggedStacs) draggedStacs = this._originStacs

    //カードを追加
    draggedStacs.addCards(this._draggingCards.children.slice() as Array<CardView>)

    //Stacsの一番上を表にする
    this._stackArea.children.forEach((stacs: Stacs) => {
      stacs.open()
    })
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 FINISH
  // --------------------------------------------------------------------------------------------------------------------------------------

  /**
   * ゲーム終了
   */
  finish() {
    //キャラクター表示
    gsap.to(this._character, {
      y: 0,
      duration: 0.5,
      ease: 'back.out(1.7)',
      onStart: () => {
        this._character.visible = true
      },
      onComplete: () => {
        AssetsManager.instance.playSound('sound_cheers')
        this._serif.visible = true
        this._retryBtn.visible = true
        this.createHearts()
      },
    })
  }

  /**
   * ハート生成
   */
  createHearts() {
    for (let i = 0; i < 5; i++) {
      const heart: HeartView = new HeartView()
      this._heartContainer.addChild(heart)
    }

    gsap.delayedCall(0.1, () => {
      this.createHearts()
    })
  }

  // --------------------------------------------------------------------------------------------------------------------------------------
  // --	 リサイズ
  // --------------------------------------------------------------------------------------------------------------------------------------

  resize() {
    this._characterContainer.x = this._app.screen.width * 0.5
    this._characterContainer.y = this._app.screen.height

    this._face.x = this._app.screen.width * 0.5
    this._face.y = this._app.screen.height * 0.5
  }

  // ---------------------------------------------------------------------------------------------------------
}

export default GameView
